/**
 * Runtime options — one resolution, one precedence rule.
 *
 * Values arrive through five channels and are resolved ONCE at init into the
 * typed `ResolvedOptions` stored on the SDK state:
 *
 *   URL `kz_*` params  >  init() options / config() store  >  script `data-kz-*` attrs
 *     >  sealed build defaults  >  defaults
 *
 * The same URL-first rule applies at the call site (e.g. `pool.fetchPost`):
 * URL `kz_*` > method args > resolved init options.
 */

import { warn } from '../util/log';
import type { ConsentInit, PrivacyInit } from './consent/port';
import type { LoadCmpMode } from './consent/engine';
import { DEFAULT_API_BASE_URL } from './config/constants';
import { readSealedOptions } from './config/sealed';
import { resolveInitDomain } from './config/domain';
import type { Pixel } from './config/types';
import { tryParseSdkPixelForConfig } from './config/pixelParse';
import {
  QueryParam,
  resolveBoolFromUrl,
  resolveDebugFromUrl,
  resolveDomainFromUrl,
  resolveIncludeDraftsFromUrl,
  resolveLocaleFromUrl,
  resolvePostIdFromUrl,
  resolveStringFromUrl,
} from './queryParams';

/* -------------------------------------------------------------------------- */
/* init() options                                                              */
/* -------------------------------------------------------------------------- */

/** Host-supplied pixel (`init({ pixels })`), merged with the active ad account pixel. */
export interface InitPixelOption {
  platform: string;
  pixelId: string;
  /** Google Ads conversion label. */
  pixelTagId?: string;
}

export interface InitOptions {
  /**
   * Website domain to load from Pool.
   * Prefer omitting this — production uses `window.location.hostname`.
   * For testing on preview/local pages, use `?kz_domain=` (always wins).
   */
  domain?: string;
  /** Override the public API base URL. Default is baked at build time via __DEFAULT_API_BASE_URL__. */
  apiBaseUrl?: string;
  /** When 'never', SDK never auto-loads InMobi even if `cmp.required`. */
  loadCmp?: LoadCmpMode;
  /**
   * Refuse to run without consent. Baked into sealed bundles, which load on
   * pages we do not own: if the page's CMP does not grant us, `init()` installs
   * nothing, sends nothing and stores nothing (see `core/init.ts`). A later
   * grant from that CMP starts it.
   */
  requireConsent?: boolean;
  /** Pin unified tracking permission for this session. Skips CMP detect/load. */
  consent?: ConsentInit;
  /** CCPA sale opt-out state at init. Uses the yoto `ccpa_opt_out` cookie. */
  privacy?: PrivacyInit;
  /** Verbose console logging. */
  debug?: boolean;
  /** Override analytics activity_id (`ac=` on /dye). */
  activityId?: string;
  /** Override analytics activity_model (`acm=` on /dye). */
  activityModel?: string;
  /** Override layout (`ly=` on /dye). */
  layout?: string;
  /** Default channel (`aci=` on /dye) when no `ch` query param is present. */
  defaultChannel?: string;
  /** Override post ID (`pid=` and `opid=` on /dye, pool auto-fetch). */
  postId?: string;
  /** Default Pool locale. Used by plug-and-play content mode when `kz_lo` is absent. */
  defaultLocale?: string;
  /** When true, Pool post fetches request draft/private-mode content (`include_drafts=1`). */
  includeDrafts?: boolean;
  /**
   * Attribution match key (`source/medium`, e.g. `fb1/paid`).
   * Precedence: this value > page `utm_source`/`utm_medium`.
   * Selects the active ad account for ecom tagging and pixel wiring.
   */
  sourceMedium?: string;
  /** When true, SDK will not load or fire any third-party pixels. */
  disablePixels?: boolean;
  /**
   * Activate the mbp passive ad-analytics module (GPT/Prebid/Voltax observation).
   * Also settable via the `data-kz-mbp` script attribute.
   */
  mbp?: boolean;
  /**
   * Count only time the tab is in the foreground towards time-on-page. Default
   * `false`, which counts elapsed time as yoto-web does. Turning it on reports
   * engaged time instead, which is not comparable with yoto-web's figure. Also
   * settable via the `data-kz-active-time-only` script attribute.
   */
  activeTimeOnly?: boolean;
  /**
   * Extra pixels to always load, merged with the active ad account's pixel
   * (deduped by `platform:pixel_id`).
   */
  pixels?: InitPixelOption[];
  /**
   * Tracker base URLs (e.g. `https://track.example.com`) that REPLACE the
   * built-in Kueez trackers. Every analytics event fans out to each entry.
   * Omit to keep the defaults (track/gtrack/otrack.kueez.net).
   */
  trackers?: string[];
  /**
   * Report analytics under this hostname (`h=` on every event) instead of
   * the config website domain. Affects ONLY the tracking envelope — the pool
   * config request keeps using `domain` / the page hostname. Useful for
   * testing the SDK on a production page without mixing its events into that
   * site's reporting. Also settable via `data-kz-tracking-domain`.
   */
  trackingDomain?: string;
  /**
   * Override website ecom account id (`ecmai` for `/assign/ad`).
   * Defaults to pool setting `ecom_account_id` (yoto `window.ecmai`).
   */
  ecomAccountId?: string;
  /**
   * When false, skips attribution ids on `/dye` and omits UTM/mbid/mbclid
   * common params (yoto `MEDIA_BUYING_MODULE_ENABLED`). Default true.
   */
  mediaBuyingEnabled?: boolean;
  /** Fired once after consent resolves and the page-view event has dispatched. */
  onReady?: () => void;
}

/** Fully-resolved runtime options — the single source of truth after init. */
export interface ResolvedOptions {
  domain: string;
  apiBaseUrl: string;
  debug: boolean;
  loadCmp: LoadCmpMode;
  /** No consent, no init. Baked into sealed bundles. */
  requireConsent: boolean;
  consent?: ConsentInit;
  privacy?: PrivacyInit;
  activityId?: string;
  activityModel?: string;
  layout?: string;
  defaultChannel?: string;
  postId?: string;
  defaultLocale: string;
  includeDrafts: boolean;
  /** Normalized `source/medium` from init, when provided. */
  sourceMedium?: string;
  disablePixels: boolean;
  /** Host opt-in for the mbp module (`init({ mbp })` / `data-kz-mbp`). */
  mbp: boolean;
  /** Count foreground time only towards time-on-page. Default `false` (elapsed). */
  activeTimeOnly: boolean;
  mediaBuyingEnabled: boolean;
  /** Override for website ecom account id used by runtime ad-tag assignment. */
  ecomAccountId?: string;
  /** Parsed init pixels (invalid entries dropped with a warning). */
  pixels: Pixel[];
  /** Normalized extra tracker base URLs (invalid entries dropped with a warning). */
  trackers: string[];
  /** Hostname override for the analytics envelope (`h=`); pool fetch unaffected. */
  trackingDomain?: string;
  onReady?: () => void;
}

/* -------------------------------------------------------------------------- */
/* Script attributes (`data-kz-*` on the sdk script tag)                       */
/* -------------------------------------------------------------------------- */

export interface KueezScriptAttrs {
  render?: string;
  domain?: string;
  layout?: string;
  postId?: string;
  defaultLocale?: string;
  apiBaseUrl?: string;
  includeDrafts?: boolean;
  mbp?: boolean;
  activeTimeOnly?: boolean;
  activityId?: string;
  activityModel?: string;
  trackingDomain?: string;
  /** Raw `data-kz-loader` value: empty/true, false, or dotted path to options. */
  loader?: string;
}

/** Not an init signal — the tag id the attribute lookup falls back to. */
const SDK_SCRIPT_ID = 'kueez-sdk';

let capturedAttrs: KueezScriptAttrs = {};
let scriptHasLoadHandler = false;

function clean(value: string | undefined): string | undefined {
  const trimmed = (value ?? '').trim();
  return trimmed || undefined;
}

/** Parse boolean script attrs (`data-kz-include-drafts`, etc.). */
export function parseBoolAttr(value: string | undefined): boolean | undefined {
  if (value === undefined) return undefined;
  const trimmed = value.trim().toLowerCase();
  if (!trimmed || trimmed === '1' || trimmed === 'true' || trimmed === 'yes') return true;
  if (trimmed === '0' || trimmed === 'false' || trimmed === 'no') return false;
  return undefined;
}

function sdkScriptElement(): HTMLScriptElement | null {
  if (typeof document === 'undefined') return null;
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement) return current;
  const byId = document.getElementById(SDK_SCRIPT_ID);
  return byId instanceof HTMLScriptElement ? byId : null;
}

/** Called once from the IIFE entry while `document.currentScript` is live. */
export function captureScriptAttrs(): KueezScriptAttrs {
  const el = sdkScriptElement();
  // An `onload` handler on the tag is the opt-out from auto-init, and it arrives
  // in two shapes: the HTML attribute, or a property assigned to a tag built in
  // script (how code that injects the SDK then calls `init()` itself). Both are
  // checked because neither covers the other — a browser compiles the attribute
  // onto the property but jsdom does not, and a property assignment never sets
  // an attribute.
  scriptHasLoadHandler =
    el !== null && (el.hasAttribute('onload') || typeof el.onload === 'function');
  if (!el) {
    capturedAttrs = {};
    return capturedAttrs;
  }
  capturedAttrs = {
    render: clean(el.dataset.kzRender),
    domain: clean(el.dataset.kzDomain),
    layout: clean(el.dataset.kzLayout),
    postId: clean(el.dataset.kzPostId),
    defaultLocale: clean(el.dataset.kzLocale),
    apiBaseUrl: clean(el.dataset.kzApiBaseUrl),
    includeDrafts: parseBoolAttr(el.getAttribute('data-kz-include-drafts') ?? undefined),
    mbp: parseBoolAttr(el.getAttribute('data-kz-mbp') ?? undefined),
    activeTimeOnly: parseBoolAttr(el.getAttribute('data-kz-active-time-only') ?? undefined),
    activityId: clean(el.dataset.kzAc),
    activityModel: clean(el.dataset.kzAcm),
    trackingDomain: clean(el.getAttribute('data-kz-tracking-domain') ?? undefined),
    loader: el.getAttribute('data-kz-loader') ?? undefined,
  };
  return capturedAttrs;
}

export function getScriptAttrs(): KueezScriptAttrs {
  return capturedAttrs;
}

/** True when the loading script tag carries an `onload` handler — the auto-init opt-out. */
export function hasScriptLoadHandler(): boolean {
  return scriptHasLoadHandler;
}

/* -------------------------------------------------------------------------- */
/* Pre-init `config()` store                                                   */
/* -------------------------------------------------------------------------- */

export type ConfigStore = Omit<InitOptions, 'onReady'>;

const configStore: ConfigStore = {};

export function setPreInitOption<K extends keyof ConfigStore>(key: K, value: ConfigStore[K]): void {
  configStore[key] = value;
}

export function getPreInitOptions(): ConfigStore {
  return configStore;
}

/* -------------------------------------------------------------------------- */
/* Resolution                                                                  */
/* -------------------------------------------------------------------------- */

function parseInitTrackers(entries: string[] | undefined): string[] {
  if (!entries || entries.length === 0) return [];
  const out: string[] = [];
  for (const entry of entries) {
    const base = (entry ?? '').trim().replace(/\/+$/, '');
    if (/^https?:\/\/.+/.test(base)) {
      if (!out.includes(base)) out.push(base);
    } else {
      warn('init tracker dropped (must be an absolute http(s) base URL)', entry);
    }
  }
  return out;
}

function parseInitPixels(entries: InitPixelOption[] | undefined): Pixel[] {
  if (!entries || entries.length === 0) return [];
  const out: Pixel[] = [];
  for (const entry of entries) {
    const candidate: Record<string, unknown> = {
      platform: entry.platform?.trim().toLowerCase(),
      pixel_id: entry.pixelId?.trim(),
    };
    if (entry.pixelTagId) candidate.pixel_tag_id = entry.pixelTagId.trim();
    const pixel = tryParseSdkPixelForConfig(candidate);
    if (pixel) out.push(pixel);
    else warn('init pixels: skipping invalid entry', entry);
  }
  return out;
}

function normalizeInitSourceMedium(value: string | undefined): string | undefined {
  const trimmed = value?.trim().toLowerCase();
  if (!trimmed || !trimmed.includes('/')) return undefined;
  return trimmed;
}

function definedEntries<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
}

/**
 * Resolve all runtime options with the uniform precedence: URL `kz_*` >
 * `init()` args > pre-init `config()` store > script attrs > sealed defaults >
 * defaults.
 */
export function resolveOptions(initArgs: InitOptions): ResolvedOptions {
  const attrs = getScriptAttrs();
  const scriptDefaults: InitOptions = {
    domain: attrs.domain,
    apiBaseUrl: attrs.apiBaseUrl,
    layout: attrs.layout,
    postId: attrs.postId,
    defaultLocale: attrs.defaultLocale,
    includeDrafts: attrs.includeDrafts,
    mbp: attrs.mbp,
    activeTimeOnly: attrs.activeTimeOnly,
    activityId: attrs.activityId,
    activityModel: attrs.activityModel,
    trackingDomain: attrs.trackingDomain,
  };

  const urlOverrides: InitOptions = {};
  const urlDebug = resolveDebugFromUrl();
  if (urlDebug !== undefined) urlOverrides.debug = urlDebug;
  const urlIncludeDrafts = resolveIncludeDraftsFromUrl();
  if (urlIncludeDrafts !== undefined) urlOverrides.includeDrafts = urlIncludeDrafts;
  const urlDomain = resolveDomainFromUrl();
  if (urlDomain) urlOverrides.domain = urlDomain;
  const urlPostId = resolvePostIdFromUrl();
  if (urlPostId) urlOverrides.postId = urlPostId;
  const urlLocale = resolveLocaleFromUrl();
  if (urlLocale) urlOverrides.defaultLocale = urlLocale;
  const urlActivityId = resolveStringFromUrl(QueryParam.ActivityId);
  if (urlActivityId) urlOverrides.activityId = urlActivityId;
  const urlActivityModel = resolveStringFromUrl(QueryParam.ActivityModel);
  if (urlActivityModel) urlOverrides.activityModel = urlActivityModel;
  const urlMbp = resolveBoolFromUrl(QueryParam.Mbp);
  if (urlMbp !== undefined) urlOverrides.mbp = urlMbp;

  const merged: InitOptions = {
    ...definedEntries(readSealedOptions() ?? {}),
    ...definedEntries(scriptDefaults),
    ...definedEntries(getPreInitOptions()),
    ...definedEntries(initArgs),
    ...urlOverrides,
  };

  return {
    domain: resolveInitDomain(merged.domain, typeof window !== 'undefined' ? window.location.hostname : ''),
    apiBaseUrl: merged.apiBaseUrl?.trim() || DEFAULT_API_BASE_URL,
    debug: merged.debug === true,
    loadCmp: merged.loadCmp ?? 'auto',
    requireConsent: merged.requireConsent === true,
    consent: merged.consent,
    privacy: merged.privacy,
    activityId: merged.activityId,
    activityModel: merged.activityModel,
    layout: merged.layout,
    defaultChannel: merged.defaultChannel,
    postId: merged.postId,
    defaultLocale: (merged.defaultLocale || 'en').trim().toLowerCase() || 'en',
    includeDrafts: merged.includeDrafts === true,
    sourceMedium: normalizeInitSourceMedium(merged.sourceMedium),
    disablePixels: merged.disablePixels === true,
    mbp: merged.mbp === true,
    activeTimeOnly: merged.activeTimeOnly === true,
    mediaBuyingEnabled: merged.mediaBuyingEnabled !== false,
    ecomAccountId: merged.ecomAccountId?.trim() || undefined,
    pixels: parseInitPixels(merged.pixels),
    trackers: parseInitTrackers(merged.trackers),
    trackingDomain: merged.trackingDomain?.trim().toLowerCase() || undefined,
    onReady: initArgs.onReady,
  };
}

/* -------------------------------------------------------------------------- */
/* Render callback (`data-kz-render`)                                          */
/* -------------------------------------------------------------------------- */

function isRenderPath(path: string): boolean {
  return /^[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*$/.test(path);
}

export function resolveRenderCallback(): ((post: unknown) => void) | null {
  const path = getScriptAttrs().render;
  if (!path) return null;
  if (!isRenderPath(path)) {
    warn(`[kueez-sdk/pool] Invalid data-kz-render callback path: ${path}`);
    return null;
  }

  let cursor: unknown = window;
  for (const segment of path.split('.')) {
    if (cursor === null || cursor === undefined) {
      warn(`[kueez-sdk/pool] data-kz-render callback not found: ${path}`);
      return null;
    }
    cursor = (cursor as Record<string, unknown>)[segment];
  }

  if (typeof cursor !== 'function') {
    warn(`[kueez-sdk/pool] data-kz-render callback is not a function: ${path}`);
    return null;
  }

  return cursor as (post: unknown) => void;
}

/** Test-only — clear captured attrs and the pre-init config store. */
export function _resetOptionsForTests(): void {
  capturedAttrs = {};
  scriptHasLoadHandler = false;
  for (const key of Object.keys(configStore) as (keyof ConfigStore)[]) {
    delete configStore[key];
  }
}
